import { FC, Fragment, useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Field, useField, useForm } from 'react-final-form';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  AdminElection,
  CandidateRace,
  RaceStatus,
  SelectedElectionItem,
  useGetRacesQuery,
  usePatchRaceMutation,
} from 'services/adminCandidatesApi';
import { showToast } from 'components/Toast';
import { SelectField } from 'components/Forms/SelectField';
import { RACE_DETAIL_CHECKS, RACE_STATUS_OPTIONS, RaceDetailField } from 'constants/candidateRaces';
import { ElectionSearchSelect } from './ElectionSearchSelect';

interface Props {
  recipientId: string;
  readOnly?: boolean;
}

export const CandidateElections: FC<Props> = ({ recipientId, readOnly = false }) => {
  const {
    data: races = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useGetRacesQuery({ recipientId });
  const racesError = (error as FetchBaseQueryError)?.data as { reason?: string } | undefined;
  const { change } = useForm();
  const {
    input: { value: newElection },
  } = useField<SelectedElectionItem | undefined>('new_election', {
    subscription: { value: true },
  });
  const [isAdding, setIsAdding] = useState(false);
  const [activeRaceId, setActiveRaceId] = useState<string | null>(null);
  const [pendingRaceId, setPendingRaceId] = useState<string | null>(null);
  const [patchRace] = usePatchRaceMutation();

  useEffect(() => {
    if (isError) {
      datadogLogs.logger.error(
        'Failed to load candidate races',
        { recipientId, status: (error as FetchBaseQueryError)?.status },
        error as unknown as Error
      );
    }
  }, [isError, error, recipientId]);

  useEffect(() => {
    races.forEach(race => {
      change(`race_status_${race.race_id}`, race.status);
      RACE_DETAIL_CHECKS.forEach(({ field }) =>
        change(`race_${field}_${race.race_id}`, race[field])
      );
    });
  }, [races, change]);

  const handleSelect = (election: AdminElection) => {
    change('new_election', {
      election_id: election.election_id,
      display_info: election.display_info,
      state: election.state,
      type: election.type,
      election_date: election.election_date,
      status: 'ACTIVE',
    });
    setIsAdding(false);
  };

  const handleRemove = () => {
    change('new_election', undefined);
    setIsAdding(false);
  };

  const toggleDetails = (raceId: string) => {
    setActiveRaceId(prev => (prev === raceId ? null : raceId));
  };

  const handleStatusChange = (race: CandidateRace, status: RaceStatus) => {
    setPendingRaceId(race.race_id);
    patchRace({ recipientId, raceId: race.race_id, body: { status } })
      .unwrap()
      .then(() => showToast('Race status updated successfully', 'success'))
      .catch(error => {
        change(`race_status_${race.race_id}`, race.status);
        datadogLogs.logger.error(
          'Failed to update race status',
          { recipientId, raceId: race.race_id },
          error as Error
        );
        showToast('Something went wrong updating the race. Please try again.', 'error');
      })
      .finally(() => setPendingRaceId(null));
  };

  const handleDetailChange = (race: CandidateRace, field: RaceDetailField, value: boolean) => {
    setPendingRaceId(race.race_id);
    patchRace({ recipientId, raceId: race.race_id, body: { [field]: value } })
      .unwrap()
      .then(() => showToast('Race details updated successfully', 'success'))
      .catch(error => {
        change(`race_${field}_${race.race_id}`, race[field]);
        datadogLogs.logger.error(
          'Failed to update race details',
          { recipientId, raceId: race.race_id, field },
          error as Error
        );
        showToast('Something went wrong updating the race. Please try again.', 'error');
      })
      .finally(() => setPendingRaceId(null));
  };

  return (
    <>
      <p className="oath-eyebrow inkBlue mb-2">Elections</p>

      {isLoading ? (
        <span className="oath-bodysmall gray">Loading elections...</span>
      ) : isError ? (
        <div className="oath-bodysmall">
          <span className="error">
            Couldn&apos;t load linked elections
            {racesError?.reason ? `: ${racesError.reason}` : '.'}
          </span>{' '}
          <button type="button" className="link-text inkBlue padding0" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : races.length === 0 ? (
        <span className="oath-bodysmall gray">Not linked to any election yet.</span>
      ) : (
        <div className="draggable-table-container">
          <table className="draggable-table">
            <tbody>
              {races.map(race => {
                const isDisabled = race.status === 'COMPLETE';
                const isOpen = activeRaceId === race.race_id;
                return (
                  <Fragment key={race.race_id}>
                    <tr
                      className={classNames({
                        active: !isDisabled,
                        'expanded-content': isOpen,
                      })}
                    >
                      <td
                        className={classNames('rec-header-info rec', {
                          active: !isDisabled,
                          expanded: isOpen,
                        })}
                      >
                        <div className="grid">
                          <span className="oath-body inkBlue">{race.election_display_info}</span>
                          <span className="oath-bodysmall gray">
                            {race.state} · {dayjs(race.election_date).format('MMM D, YYYY')}
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="race-status-select">
                            <SelectField
                              name={`race_status_${race.race_id}`}
                              label="Status"
                              options={RACE_STATUS_OPTIONS}
                              disabled={readOnly || pendingRaceId === race.race_id}
                              onChange={value => handleStatusChange(race, value as RaceStatus)}
                            />
                          </div>
                          <button
                            type="button"
                            className={classNames('oath-bodysmall link-text color-gray ml-2', {
                              active: isOpen,
                            })}
                            onClick={() => toggleDetails(race.race_id)}
                          >
                            {isOpen ? 'Close Details' : 'View Details'}
                          </button>
                        </div>
                      </td>
                      <td className="delete-td rec" />
                    </tr>
                    {isOpen && (
                      <tr>
                        <td className="rec-details-info expanded-details rec">
                          <div className="divider mb-2" />
                          <p className="oath-eyebrow inkBlue mb-2">Race details</p>
                          <div className="d-flex election-picker__checks">
                            {RACE_DETAIL_CHECKS.map(({ field, label }) => (
                              <Field
                                key={field}
                                name={`race_${field}_${race.race_id}`}
                                type="checkbox"
                              >
                                {({ input: cbInput }) => (
                                  <div className="form-check form-check-inline mr-3">
                                    <label className="form-check-label oath-bodysmall inkBlue">
                                      <input
                                        type="checkbox"
                                        checked={cbInput.checked}
                                        disabled={readOnly || pendingRaceId === race.race_id}
                                        onChange={e => {
                                          cbInput.onChange(e);
                                          handleDetailChange(race, field, e.target.checked);
                                        }}
                                      />
                                      <span className="form-check-sign" />
                                      {label}
                                    </label>
                                  </div>
                                )}
                              </Field>
                            ))}
                          </div>
                        </td>
                        <td />
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {newElection ? (
        <div className="mt-3">
          <div className="draggable-table-container">
            <table className="draggable-table">
              <tbody>
                <tr className="active expanded-content">
                  <td className="rec-header-info rec active expanded">
                    <div className="grid">
                      <span className="oath-body inkBlue">{newElection.display_info}</span>
                      <span className="oath-bodysmall gray">
                        {newElection.state} ·{' '}
                        {dayjs(newElection.election_date).format('MMM D, YYYY')} · Saved when you
                        click Update
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="race-status-select">
                        <SelectField
                          name="new_election.status"
                          label="Status"
                          options={RACE_STATUS_OPTIONS}
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="delete-td rec">
                    <button
                      type="button"
                      className="oath-bodysmall link-text gray"
                      onClick={handleRemove}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className="rec-details-info expanded-details rec">
                    <div className="divider mb-2" />
                    <p className="oath-eyebrow inkBlue mb-2">Race details</p>
                    <div className="d-flex election-picker__checks">
                      {RACE_DETAIL_CHECKS.map(({ field, label }) => (
                        <Field key={field} name={`new_election.${field}`} type="checkbox">
                          {({ input: cbInput }) => (
                            <div className="form-check form-check-inline mr-3">
                              <label className="form-check-label oath-bodysmall inkBlue">
                                <input
                                  type="checkbox"
                                  checked={cbInput.checked}
                                  onChange={cbInput.onChange}
                                />
                                <span className="form-check-sign" />
                                {label}
                              </label>
                            </div>
                          )}
                        </Field>
                      ))}
                    </div>
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : isAdding ? (
        <div className="row mt-3">
          <div className="col-md-6 padding0">
            <div className="form-input">
              <label htmlFor="add-election-picker">Election *</label>
              <ElectionSearchSelect
                inputId="add-election-picker"
                onSelect={handleSelect}
                excludeElectionIds={races.map(race => race.election_id)}
              />
            </div>
          </div>
        </div>
      ) : !isError && !readOnly ? (
        <button
          type="button"
          className="oath-bodysmall link-text inkBlue padding0 mt-2"
          onClick={() => setIsAdding(true)}
        >
          + Add election
        </button>
      ) : null}
    </>
  );
};
